/**
 * AdminSettingsPage.js — /admin/settings
 *
 * RC 1.38.A.UI — Admin Settings (Slice 2, read-only).
 *
 * Operator-facing one-screen view of every runtime-tunable
 * configuration row in `app_settings`. Backed by the existing
 * `GET /api/admin/settings` endpoint (no schema change, no new
 * route). The 5 RC 1.38.A keys are pinned to the top with friendly
 * labels; everything else surfaced via app_settings is rendered
 * underneath in a generic "Other" section so operators always see
 * the full picture.
 *
 * Out-of-scope for this slice (intentional):
 *   - No editing UI. Edits go through the existing
 *     `PUT /api/admin/settings/{key}` endpoint via curl / API client.
 *   - No price-table rendering (`/api/admin/credit-prices/{action}`
 *     remains its own surface).
 *   - No employer / matching / PII / Stripe / pricing change.
 *
 * Access:
 *   - Same gating pattern as the other admin pages — `WorkspaceGuard
 *     workspace="admin"` at the route level + `require_admin` server
 *     side. Non-admins see a clean "Admin only" message.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, ShieldAlert, Settings as SettingsIcon,
  Gauge, Zap, Users, Target, Database as DbIcon, Info,
  Pencil, Check, X as XIcon, AlertTriangle,
} from 'lucide-react';

// RC 1.38.A — pinned keys with operator-friendly metadata. Order
// here is the order rendered. Anything from the API outside this
// list is appended to the "Other settings" section.
//
// `editable: true` opts a key into the Slice 3 inline-edit affordance.
// The 5 RC 1.38.A operational levers are the only keys allowed —
// edits to any other key require the curl path. `min`/`max` are the
// client-side guardrails; the backend SQL function (`require_admin`
// + `app_settings_upsert`) is the authoritative gate.
//
// `confirmOnSave: true` triggers a "you are about to change a
// spend-bounded cap" affirm-step on the three automation caps that
// directly bound LLM spend.
const PINNED_KEYS = [
  {
    key: 'limits.free_daily_analyze_fit',
    label: 'Free-tier daily Analyse-Fit quota',
    summary: 'Per-user daily quota of free job analyses for Base / no-entitlement users. Resets at 00:00 UTC.',
    icon: Gauge,
    unit: '',
    editable: true,
    valueType: 'int',
    min: 0,
    max: 50,
    step: 1,
    confirmOnSave: false,
  },
  {
    key: 'automation.flow_queue_threshold',
    label: 'Flow queue admission threshold',
    summary: 'Minimum fit_score (0–100) required to admit a job into the Flow automation_queue.',
    icon: Target,
    unit: '/ 100',
    editable: true,
    valueType: 'int',
    min: 0,
    max: 100,
    step: 1,
    confirmOnSave: false,
  },
  {
    key: 'automation.max_users_per_tick',
    label: 'Max users per automation tick',
    summary: 'Hard cap on the number of users processed in a single Flow automation tick. Settings can lower but never raise the in-code ceiling.',
    icon: Users,
    unit: '',
    editable: true,
    valueType: 'int',
    min: 0,
    max: 20, // matches in-code MAX_USERS_PER_TICK ceiling
    step: 1,
    confirmOnSave: true,
    spendBounded: true,
  },
  {
    key: 'automation.max_jobs_per_user_per_tick',
    label: 'Max jobs per user per tick',
    summary: 'Hard cap on jobs analysed per user per tick. Bounds LLM spend per tick.',
    icon: Zap,
    unit: '',
    editable: true,
    valueType: 'int',
    min: 0,
    max: 3, // matches in-code MAX_JOBS_PER_USER_PER_TICK ceiling
    step: 1,
    confirmOnSave: true,
    spendBounded: true,
  },
  {
    key: 'automation.candidate_pool_size',
    label: 'Candidate-pool window size',
    summary: 'Per-tick candidate-jobs read window before per-user filtering.',
    icon: DbIcon,
    unit: '',
    editable: true,
    valueType: 'int',
    min: 0,
    max: 100, // matches in-code CANDIDATE_POOL_SIZE ceiling
    step: 1,
    confirmOnSave: true,
    spendBounded: true,
  },
  // RC 1.46.REJECT-GATE.SETTINGS (2026-05-02) — operator-tunable
  // Analyse Fit floor used by the pre-CV-generation reject gate.
  // RC 1.46.REJECT-GATE.HOLISTIC-ONLY (2026-05-02 follow-up):
  // gate is now tied to `fit_score` (holistic Analyse-Fit) ONLY.
  // The strict `cv_sub_score` (NDOR CV verdict / text-evidence
  // grader) is NOT used for reject decisions because it can
  // legitimately be very low even when the candidate IS a strong
  // fit — that's a CV-writing gap, not a candidate-fit gap.
  // Kept INDEPENDENT from `automation.flow_queue_threshold`
  // — that's a backend batch knob; this is a candidate-facing UX
  // gate. Default 60. `confirmOnSave: false` because the change
  // affects future generations only — no spend implication on save.
  {
    key: 'LIMITS.CV_GENERATION_MIN_FIT',
    label: 'Analyse Fit minimum score',
    summary: 'Minimum Overall Fit / Analyse-Fit score (0–100, holistic) required to allow tailored-CV generation. Tied to fit_score, NOT the strict NDOR CV verdict (cv_sub_score) — a low CV verdict reflects CV-writing quality, not candidate suitability. Below this floor the wizard refuses to call the LLM and asks the user to pick a better-matched role or upload a more relevant Source CV. final_decision == "reject" still blocks regardless of this value.',
    icon: Target,
    unit: '/ 100',
    editable: true,
    valueType: 'int',
    min: 0,
    max: 100,
    step: 1,
    confirmOnSave: false,
  },
];

const PINNED_KEY_SET = new Set(PINNED_KEYS.map((p) => p.key));

function fmtTs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function settingDisplayValue(row) {
  if (row?.value_int !== null && row?.value_int !== undefined) {
    return { value: String(row.value_int), kind: 'int' };
  }
  if (row?.value_text) {
    return { value: row.value_text, kind: 'text' };
  }
  if (row?.value_json !== null && row?.value_json !== undefined) {
    try {
      return { value: JSON.stringify(row.value_json, null, 2), kind: 'json' };
    } catch (_e) {
      return { value: '[unserialisable JSON]', kind: 'json' };
    }
  }
  return { value: '—', kind: 'empty' };
}

/** One pinned RC 1.38.A row rendered as a premium operator card. */
function PinnedSettingCard({ meta, row, fallback, onSaved }) {
  const Icon = meta.icon || SettingsIcon;
  const present = !!row;
  const display = present ? settingDisplayValue(row) : { value: String(fallback ?? '—'), kind: 'fallback' };

  // Edit state
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(present ? String(row.value_int ?? '') : String(fallback ?? ''));
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Reset draft whenever the row changes (e.g. after a successful save +
  // refresh, or if another tab edits the same key).
  useEffect(() => {
    if (!editing) {
      setDraft(present ? String(row.value_int ?? '') : String(fallback ?? ''));
    }
  }, [row, present, fallback, editing]);

  const startEdit = () => {
    setErr(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setErr(null);
    setDraft(present ? String(row.value_int ?? '') : String(fallback ?? ''));
  };

  const validate = () => {
    if (draft === '' || draft === null || draft === undefined) {
      return 'Value required.';
    }
    const n = Number(draft);
    if (!Number.isFinite(n)) return 'Must be a number.';
    if (!Number.isInteger(n)) return 'Must be an integer.';
    if (typeof meta.min === 'number' && n < meta.min) return `Must be ≥ ${meta.min}.`;
    if (typeof meta.max === 'number' && n > meta.max) return `Must be ≤ ${meta.max} (in-code ceiling).`;
    return null;
  };

  const requestSave = () => {
    const e = validate();
    if (e) {
      setErr(e);
      return;
    }
    if (meta.confirmOnSave) {
      setConfirmOpen(true);
    } else {
      void doSave();
    }
  };

  const doSave = async () => {
    setSaving(true);
    setErr(null);
    setConfirmOpen(false);
    const n = Number(draft);
    try {
      const res = await apiFetch(`/api/admin/settings/${encodeURIComponent(meta.key)}`, {
        method: 'PUT',
        body: { value_int: n },
      });
      // Backend returns {"setting": {...}} on success.
      const updated = res?.setting || res;
      toast.success(`Updated · ${meta.label}`, {
        description: `${meta.key} → ${n}`,
      });
      setEditing(false);
      // Surface the fresh row up to the page so the card re-renders
      // immediately. The page also re-fetches the full list (cheap +
      // cache-busts the 60s TTL transparently).
      if (onSaved) onSaved(updated);
    } catch (e) {
      const msg = e?.message || 'Save failed.';
      setErr(msg);
      toast.error('Save failed', { description: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      className="border-ndor-edge bg-ndor-surface brand-card-hover"
      data-testid={`admin-setting-card-${meta.key}`}
    >
      <CardContent className="p-5 sm:p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-ndor-info-light text-ndor-info flex items-center justify-center border border-ndor-edge shrink-0">
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3
                className="text-sm sm:text-[15px] font-serif-display font-semibold text-ndor-ink leading-snug"
                data-testid={`admin-setting-label-${meta.key}`}
              >
                {meta.label}
              </h3>
              <p
                className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui mt-0.5 break-all"
                data-testid={`admin-setting-key-${meta.key}`}
              >
                {meta.key}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {present ? (
              <Badge
                variant="outline"
                className="border-emerald-500/40 bg-emerald-500/10 text-emerald-200 text-[10px] uppercase tracking-wide whitespace-nowrap"
                data-testid={`admin-setting-source-${meta.key}`}
              >
                From DB
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="border-amber-500/40 bg-amber-500/10 text-amber-200 text-[10px] uppercase tracking-wide whitespace-nowrap"
                data-testid={`admin-setting-source-${meta.key}`}
              >
                Default
              </Badge>
            )}
            {meta.editable && !editing ? (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-ndor-ink-muted hover:text-ndor-ink"
                onClick={startEdit}
                aria-label={`Edit ${meta.label}`}
                title="Edit value"
                data-testid={`admin-setting-edit-${meta.key}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </div>
        </div>

        {/* Value display OR inline editor */}
        {!editing ? (
          <div className="flex items-baseline gap-2">
            <p
              className="text-3xl sm:text-4xl font-serif-display font-semibold text-ndor-ink tabular-nums"
              data-testid={`admin-setting-value-${meta.key}`}
            >
              {display.value}
            </p>
            {meta.unit ? (
              <span className="text-sm text-ndor-ink-muted font-mono-ui">{meta.unit}</span>
            ) : null}
          </div>
        ) : (
          <div className="space-y-2.5" data-testid={`admin-setting-editor-${meta.key}`}>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                inputMode="numeric"
                step={meta.step ?? 1}
                min={meta.min}
                max={meta.max}
                value={draft}
                onChange={(e) => { setDraft(e.target.value); setErr(null); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') requestSave();
                  if (e.key === 'Escape') cancelEdit();
                }}
                disabled={saving}
                autoFocus
                className="h-11 w-28 text-2xl font-serif-display font-semibold tabular-nums"
                aria-label={`${meta.label} value`}
                data-testid={`admin-setting-input-${meta.key}`}
              />
              {meta.unit ? (
                <span className="text-sm text-ndor-ink-muted font-mono-ui">{meta.unit}</span>
              ) : null}
              <div className="flex items-center gap-1 ml-auto">
                <Button
                  size="sm"
                  variant="default"
                  onClick={requestSave}
                  disabled={saving}
                  className="h-9 bg-ndor-ink text-ndor-canvas hover:bg-ndor-ink/90"
                  data-testid={`admin-setting-save-${meta.key}`}
                >
                  {saving ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  ) : (
                    <Check className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={cancelEdit}
                  disabled={saving}
                  className="h-9"
                  data-testid={`admin-setting-cancel-${meta.key}`}
                >
                  <XIcon className="h-3.5 w-3.5 mr-1" />
                  Cancel
                </Button>
              </div>
            </div>
            <p className="text-[11px] text-ndor-ink-muted font-mono-ui">
              Allowed range: {meta.min}–{meta.max}
              {meta.spendBounded ? ' (in-code ceiling — settings can lower spend but not exceed it)' : ''}
            </p>
            {err ? (
              <p
                className="text-[12px] text-ndor-error flex items-center gap-1.5"
                data-testid={`admin-setting-error-${meta.key}`}
              >
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                {err}
              </p>
            ) : null}
          </div>
        )}

        <p
          className="text-xs sm:text-sm text-ndor-ink-secondary leading-relaxed"
          data-testid={`admin-setting-summary-${meta.key}`}
        >
          {meta.summary}
        </p>

        {present && row.description && row.description !== meta.summary ? (
          <p className="text-[11px] text-ndor-ink-muted leading-relaxed border-t border-ndor-edge pt-3">
            <span className="uppercase tracking-[0.18em] font-mono-ui mr-1.5">DB note:</span>
            {row.description}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1 text-[11px] text-ndor-ink-muted font-mono-ui">
          <span data-testid={`admin-setting-updated-${meta.key}`}>
            Updated: {present ? fmtTs(row.updated_at) : 'never (default)'}
          </span>
          {present && row.updated_by ? (
            <span className="truncate max-w-[260px]" title={row.updated_by}>
              By: {row.updated_by.slice(0, 8)}…
            </span>
          ) : null}
        </div>
      </CardContent>

      {/* Confirm modal for spend-bounded automation caps */}
      <Dialog open={confirmOpen} onOpenChange={(o) => !saving && setConfirmOpen(o)}>
        <DialogContent
          className="sm:max-w-[440px]"
          data-testid={`admin-setting-confirm-${meta.key}`}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Confirm change
            </DialogTitle>
            <DialogDescription className="text-sm text-ndor-ink-secondary leading-relaxed pt-2 space-y-2">
              <span className="block">
                You're about to change <strong className="text-ndor-ink">{meta.label}</strong>.
              </span>
              <span className="block">
                <code className="text-[11px] px-1.5 py-0.5 rounded bg-ndor-surface-alt">{meta.key}</code>
                : <span className="font-mono-ui">{display.value}</span> → <span className="font-mono-ui font-semibold text-ndor-ink">{draft}</span>
              </span>
              {meta.spendBounded ? (
                <span className="block text-[12px] text-amber-200 bg-amber-500/10 border border-amber-500/40 rounded p-2 mt-2">
                  This is a spend-bounded automation cap. Settings can lower
                  spend but the in-code ceiling ({meta.max}) still applies as
                  the final guard.
                </span>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={saving}
              data-testid={`admin-setting-confirm-cancel-${meta.key}`}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={doSave}
              disabled={saving}
              className="bg-ndor-ink text-ndor-canvas hover:bg-ndor-ink/90"
              data-testid={`admin-setting-confirm-save-${meta.key}`}
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : null}
              Confirm save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/** Generic row for any non-pinned `app_settings` entry. */
function GenericSettingRow({ row }) {
  const display = settingDisplayValue(row);
  return (
    <div
      className="border border-ndor-edge bg-ndor-surface rounded-lg p-4 space-y-2"
      data-testid={`admin-other-setting-${row.key}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <p className="text-[11px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui break-all">
          {row.key}
        </p>
        <Badge
          variant="outline"
          className="border-ndor-edge text-[10px] uppercase tracking-wide w-fit"
        >
          {display.kind}
        </Badge>
      </div>
      <pre className="text-sm text-ndor-ink whitespace-pre-wrap break-words tabular-nums font-mono-ui m-0">
        {display.value}
      </pre>
      {row.description ? (
        <p className="text-xs text-ndor-ink-secondary leading-relaxed">
          {row.description}
        </p>
      ) : null}
      <p className="text-[11px] text-ndor-ink-muted font-mono-ui pt-1 border-t border-ndor-edge">
        Updated: {fmtTs(row.updated_at)}
      </p>
    </div>
  );
}

export default function AdminSettingsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      const data = await apiFetch('/api/admin/settings');
      // Backend returns either {settings: [...]} or [...] depending on
      // age — be lenient on the shape so a future flatten doesn't break.
      const list = Array.isArray(data) ? data : (data?.settings || []);
      setRows(list);
      setFetchedAt(new Date());
    } catch (e) {
      if (e?.status === 403) {
        setForbidden(true);
      } else {
        setError(e?.message || 'Failed to load settings.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Index by key for quick PINNED_KEYS lookup; preserve original
  // ordering for the "Other" tail.
  const byKey = useMemo(() => {
    const m = new Map();
    for (const r of rows) m.set(r.key, r);
    return m;
  }, [rows]);

  const otherRows = useMemo(
    () => rows.filter((r) => !PINNED_KEY_SET.has(r.key)),
    [rows],
  );

  if (forbidden) {
    return (
      <div
        className="flex flex-col items-center justify-center py-16 px-4 text-center space-y-3"
        data-testid="admin-settings-forbidden"
      >
        <ShieldAlert className="h-10 w-10 text-ndor-warning" />
        <h2 className="text-xl font-serif-display font-semibold text-ndor-ink">
          Admin only
        </h2>
        <p className="text-sm text-ndor-ink-secondary max-w-md">
          You don't have permission to view this page. If you believe this
          is wrong, ask another admin to grant your account
          <code className="mx-1 px-1 py-0.5 rounded bg-ndor-surface-alt text-xs">profiles.is_admin = true</code>
          via the database.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="admin-settings-page" className="space-y-6 sm:space-y-8">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-ndor-gold font-mono-ui">
            Admin · Operations
          </p>
          <h1 className="mt-1 text-2xl sm:text-3xl font-serif-display font-semibold tracking-tight text-ndor-ink">
            Runtime Settings
          </h1>
          <p className="mt-1.5 text-sm text-ndor-ink-secondary max-w-2xl">
            Read-only view of <code className="px-1 py-0.5 rounded bg-ndor-surface-alt text-xs">app_settings</code>.
            The 5 RC 1.38.A operational keys are pinned at the top. Edits are
            still made via{' '}
            <code className="px-1 py-0.5 rounded bg-ndor-surface-alt text-xs">PUT /api/admin/settings/{`{key}`}</code>{' '}
            (admin auth required).
          </p>
        </div>
        <div className="flex items-center gap-3">
          {fetchedAt ? (
            <span className="text-[11px] text-ndor-ink-muted font-mono-ui hidden sm:inline">
              Fetched: {fetchedAt.toLocaleTimeString()}
            </span>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            className="border-ndor-edge"
            data-testid="admin-settings-refresh"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </header>

      {/* Error banner */}
      {error ? (
        <div
          className="rounded-lg border border-ndor-error-border bg-ndor-error-light text-ndor-error p-4 text-sm"
          data-testid="admin-settings-error"
        >
          {error}
        </div>
      ) : null}

      {/* Pinned RC 1.38.A keys */}
      <section
        className="space-y-3 sm:space-y-4"
        data-testid="admin-settings-pinned"
      >
        <div className="flex items-center gap-2">
          <h2 className="text-sm sm:text-base font-serif-display font-semibold text-ndor-ink">
            RC 1.38.A operational levers
          </h2>
          <Badge variant="outline" className="border-ndor-edge text-[10px] uppercase tracking-wide">
            5 keys
          </Badge>
        </div>

        {loading && rows.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PINNED_KEYS.map((meta) => (
              <Card
                key={meta.key}
                className="border-ndor-edge bg-ndor-surface animate-pulse h-44"
                aria-hidden
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PINNED_KEYS.map((meta) => (
              <PinnedSettingCard
                key={meta.key}
                meta={meta}
                row={byKey.get(meta.key)}
                onSaved={() => { void load(); }}
              />
            ))}
          </div>
        )}
      </section>

      {/* Other (non-pinned) settings */}
      {otherRows.length > 0 ? (
        <section
          className="space-y-3 sm:space-y-4"
          data-testid="admin-settings-other"
        >
          <div className="flex items-center gap-2">
            <h2 className="text-sm sm:text-base font-serif-display font-semibold text-ndor-ink">
              Other settings
            </h2>
            <Badge variant="outline" className="border-ndor-edge text-[10px] uppercase tracking-wide">
              {otherRows.length} key{otherRows.length === 1 ? '' : 's'}
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {otherRows.map((r) => (
              <GenericSettingRow key={r.key} row={r} />
            ))}
          </div>
        </section>
      ) : null}

      {/* Help footer */}
      <section
        className="rounded-lg border border-ndor-edge bg-ndor-surface-alt/50 p-4 sm:p-5 flex items-start gap-3"
        data-testid="admin-settings-help"
      >
        <Info className="h-4 w-4 text-ndor-info shrink-0 mt-0.5" />
        <div className="text-xs sm:text-sm text-ndor-ink-secondary space-y-1.5">
          <p>
            <span className="font-medium text-ndor-ink">Inline editing for the 6 operational levers</span>{' '}
            is enabled. Changes go through{' '}
            <code className="px-1 py-0.5 rounded bg-ndor-surface-alt text-xs">
              PUT /api/admin/settings/&#123;key&#125;
            </code>{' '}
            (admin-only, server-side validated). Spend-bounded automation
            caps require an explicit confirmation step. The 60-second
            per-process settings cache is invalidated automatically on
            each save.
          </p>
          <p>
            Any other <code className="px-1 py-0.5 rounded bg-ndor-surface-alt text-xs">app_settings</code>{' '}
            row remains read-only here; use curl for those:
          </p>
          <pre className="rounded bg-ndor-surface border border-ndor-edge p-2.5 text-[11px] text-ndor-ink overflow-x-auto font-mono-ui m-0">
{`curl -X PUT "$API_URL/api/admin/settings/<key>" \\
     -H "Authorization: Bearer $ADMIN_JWT" \\
     -H "Content-Type: application/json" \\
     -d '{"value_int": 7}'`}
          </pre>
        </div>
      </section>
    </div>
  );
}
